import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  DocumentData,
  writeBatch
} from 'firebase/firestore';
import { db } from './config';
import { 
  saveRealtimeDonation, 
  updateRealtimeDonationStatus,
  clearAllRealtimeDonations 
} from './realtimeDonationService';

// Donation interface
export interface Donation {
  id?: string;
  name: string;
  phone: string;
  amount: number;
  isRecurring: boolean;
  status: 'pending' | 'completed';
  createdAt: string;
}

// Collection reference
const DONATIONS_COLLECTION = 'donations';

/**
 * Save a new donation to Firestore and Realtime Database
 */
export const saveNewDonation = async (donationData: Omit<Donation, 'id'>): Promise<string> => {
  try {
    // Save to Firestore first
    const docRef = await addDoc(collection(db, DONATIONS_COLLECTION), {
      ...donationData,
      createdAt: Timestamp.now()
    });
    
    const firestoreId = docRef.id;
    
    // Then save to Realtime Database for real-time updates
    await saveRealtimeDonation(donationData, firestoreId);
    
    return firestoreId;
  } catch (error) {
    console.error('Error saving donation:', error);
    throw error;
  }
};

/**
 * Get all donations from Firestore, ordered by creation date
 */
export const getAllDonations = async (): Promise<Donation[]> => {
  try {
    const donationsQuery = query(
      collection(db, DONATIONS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(donationsQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data() as Omit<Donation, 'id'>,
      createdAt: doc.data().createdAt.toDate().toISOString()
    }));
  } catch (error) {
    console.error('Error getting donations:', error);
    throw error;
  }
};

/**
 * Update donation status in both Firestore and Realtime Database
 */
export const updateDonationStatus = async (
  donationId: string, 
  status: 'pending' | 'completed',
  realtimeDbId?: string,
  amount?: number
): Promise<void> => {
  try {
    // Update Firestore
    const donationRef = doc(db, DONATIONS_COLLECTION, donationId);
    await updateDoc(donationRef, { status });
    
    // If we have the realtime DB ID and amount, update Realtime Database as well
    if (realtimeDbId && amount !== undefined) {
      await updateRealtimeDonationStatus(realtimeDbId, status, amount);
    }
  } catch (error) {
    console.error('Error updating donation status:', error);
    throw error;
  }
};

/**
 * Get donation statistics (total collected and target amount)
 */
export const getDonationStats = async (): Promise<{ totalCollected: number; targetAmount: number }> => {
  try {
    // For demo purposes, hardcoded target amount
    const targetAmount = 100000; // ₹1,00,000
    
    // Get completed donations
    const completedDonationsQuery = query(
      collection(db, DONATIONS_COLLECTION),
      where('status', '==', 'completed')
    );
    
    const querySnapshot = await getDocs(completedDonationsQuery);
    
    // Sum up the amounts
    const totalCollected = querySnapshot.docs.reduce((total, doc) => {
      const data = doc.data() as DocumentData;
      return total + (data.amount || 0);
    }, 0);
    
    return {
      totalCollected,
      targetAmount
    };
  } catch (error) {
    console.error('Error getting donation stats:', error);
    // Return defaults if error
    return {
      totalCollected: 0,
      targetAmount: 100000
    };
  }
};

/**
 * Delete all donations from both Firestore and Realtime Database
 */
export const deleteAllDonations = async (): Promise<void> => {
  try {
    // Get all donations from Firestore
    const donationsQuery = query(collection(db, DONATIONS_COLLECTION));
    const querySnapshot = await getDocs(donationsQuery);
    
    // Use a batch to delete all Firestore documents
    const batch = writeBatch(db);
    querySnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    // Commit the batch
    await batch.commit();
    
    // Clear all donations from Realtime Database
    await clearAllRealtimeDonations();
    
    console.log('All donations successfully deleted from Firestore and Realtime Database');
  } catch (error) {
    console.error('Error deleting all donations:', error);
    throw error;
  }
};
